import { TRACKER_GROUP_KEY } from '@shared/flow/tracker';
import type { FilterFieldCatalogEntry } from '@shared/flow/filter-fields';
import type { FlowTableConfig } from './types';
import { catalogOrdered } from './table-core/field-sections';

/**
 * The Group-by picker's option list — extracted from `FlowTable.tsx` (QA
 * batch 3, fix-plan-batch3.md D-F) so `signalsTableConfig.test.tsx`'s forward
 * coverage test ("every key the picker offers has a non-`—` blank label")
 * can compute the SAME list a mount actually renders, rather than a
 * hand-maintained copy that drifts from the real predicate.
 *
 * `tracker` is deliberately absent from `config.columns` (dropped from the
 * field catalogs in U7 — it isn't a SELECT-able/filterable field, it's a
 * derived bucket label), so it can't be picked up by deriving this list from
 * `config.columns` the way every other groupable field is. It also can't
 * come from `config.filterFields` — grouping on a non-SELECT-aliased field
 * breaks the keyset cursor on page 2 (the F4 bug class) — so it's appended
 * explicitly, gated on `config.tracker` the same way the chip column itself
 * is gated.
 */
export function groupableColumnsOf<Row>(
  config: Pick<FlowTableConfig<Row>, 'columns' | 'tracker' | 'groupOnlyKeys'>,
): { key: string; label: string }[] {
  const base = config.columns
    .filter(c => c.groupable === true || (!c.noFilter && !c.noGroup && c.type !== 'number' && c.type !== 'date'))
    .map(c => ({ key: c.key, label: c.label }));
  const withTracker = config.tracker
    ? [...base, { key: TRACKER_GROUP_KEY, label: 'Tracker Status' }]
    : base;
  // QA batch 3, D-D: group-only keys (not columns at all — `config.groupOnlyKeys`, `types.ts`) append the same way the tracker key does.
  const withGroupOnly = config.groupOnlyKeys ? [...withTracker, ...config.groupOnlyKeys] : withTracker;
  return withGroupOnly.sort((a, b) => a.label.localeCompare(b.label));
}

/** One Group-picker entry: the legacy `{ key, label }` plus the heading it prints under. */
export interface GroupableOption {
  key: string;
  label: string;
  group: string;
  /** Example line under the label (`fieldExamples` of the catalog entry) — absent when the field has none. */
  hint?: string;
}

/**
 * The V2 Group picker's list (Signals V3 PR 1, U2): the SAME keys
 * `groupableColumnsOf` offers (same predicate — grouping on a non-SELECT-aliased
 * field still breaks the keyset cursor), but ordered by `config.filterFields`
 * catalog order and carrying each entry's catalog `group`, so Group prints the
 * same headings in the same order as Filter. Tracker/group-only keys the
 * catalog doesn't carry trail under "Grouping". `groupableColumnsOf`
 * stays for V1 `FlowTable`.
 */
export function groupableOptionsOf<Row>(
  config: Pick<FlowTableConfig<Row>, 'columns' | 'tracker' | 'groupOnlyKeys'> & { filterFields?: readonly FilterFieldCatalogEntry[] },
): GroupableOption[] {
  return catalogOrdered(groupableColumnsOf(config), config.filterFields ?? [], { trailingGroup: 'Grouping' });
}
